import React from 'react';
import PropTypes from 'prop-types';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { RadioGroup } from '../radioInput/RadioInput';
import Button from '../button/Button';
import * as classes from './SmeLendingForm.module.scss';

const questions = {
  isAccountHolder: 'Does your business have a Starling business account at the moment?',
  primaryBankAccount: 'Is Starling your primary business account?',
  tradingForTwoYears: 'Has your business been continuously trading for at least two years?',
  limitedCompanyOrPartnership: 'Is your business a private limited company or a limited liability partnership?'
};

const createValidator = (config) => {
  return yup.object().shape({
    isAccountHolder: yup.string().when('$productName', {
      is: 'credit card',
      then: yup
        .string()
        .typeError('Please select an answer')
        .oneOf(['Yes'], `Unfortunately Starling only offers credit cards to existing Starling customers`),
      otherwise: yup
        .string()
        .typeError('Please select an answer')
        .oneOf(['Yes'], `You must have an open Starling business account in order to apply`)
    }),
    primaryBankAccount: yup
      .string()
      .when(['isAccountHolder', '$productName'], {
        is: (isAccountHolder, productName) => {
          return isAccountHolder === 'Yes' && productName === 'credit card';
        },
        then: yup
          .string()
          .typeError('Please select an answer')
          .oneOf(['STARLING'], `Unfortunately Starling only offers credit cards to existing Starling customers`)
      })
      .when(['isAccountHolder', '$productName'], {
        is: (isAccountHolder, productName) => {
          return isAccountHolder === 'Yes' && productName !== 'credit card';
        },
        then: yup.string().typeError('Please select an answer')
      })
      .default('OTHER'),
    tradingForTwoYears: yup
      .string()
      .typeError('Please select an answer')
      .oneOf(
        ['Yes'],
        `Unfortunately Starling only offers business ${config.productName}s to companies who have been trading for at least two years`
      ),
    limitedCompanyOrPartnership: yup
      .string()
      .typeError('Please select an answer')
      .oneOf(
        ['Yes'],
        `Unfortunately Starling only offers business ${config.productName}s to Limited Companies and LLPs`
      )
  });
};

function Step2({ config, onSubmit }) {
  const {
    register,
    handleSubmit,
    formState: { errors },
    watch
  } = useForm({
    mode: 'onChange',
    resolver: yupResolver(createValidator(config)),
    context: {
      productName: config.productName
    },
    shouldUnregister: true
  });
  const isAccountHolder = watch('isAccountHolder');

  return (
    <form
      className={classes.step}
      onSubmit={handleSubmit((data) => {
        const isAccountHolder = data.isAccountHolder === 'Yes';

        onSubmit(
          {
            isAccountHolder,
            primaryBankAccount: data.primaryBankAccount,
            filterQuestions: Object.keys(data)
              .filter((key) => key !== 'isAccountHolder' && key !== 'primaryBankAccount')
              .map((key) => ({
                question: questions[key],
                answer: data[key]
              }))
          },
          3
        );
      })}
    >
      <RadioGroup
        label={questions.isAccountHolder}
        error={errors.isAccountHolder}
        options={[
          { label: 'Yes', value: 'Yes' },
          { label: 'No', value: 'No' }
        ]}
        {...register('isAccountHolder')}
      />

      {isAccountHolder === 'Yes' ? (
        <RadioGroup
          label={questions.primaryBankAccount}
          error={errors.primaryBankAccount}
          options={[
            { label: 'Yes', value: 'STARLING' },
            { label: 'No', value: 'OTHER' }
          ]}
          {...register('primaryBankAccount')}
        />
      ) : null}

      <RadioGroup
        label={questions.tradingForTwoYears}
        error={errors.tradingForTwoYears}
        options={[
          { label: 'Yes', value: 'Yes' },
          { label: 'No', value: 'No' }
        ]}
        {...register('tradingForTwoYears')}
      />

      <RadioGroup
        label={questions.limitedCompanyOrPartnership}
        error={errors.limitedCompanyOrPartnership}
        options={[
          { label: 'Yes', value: 'Yes' },
          { label: 'No', value: 'No' }
        ]}
        {...register('limitedCompanyOrPartnership')}
      />

      <Button variant="secondary">Next</Button>
    </form>
  );
}
Step2.propTypes = {
  config: PropTypes.object,
  onSubmit: PropTypes.func
};

export default Step2;
